"use client";

import {
  CheckCircle,
  Hourglass,
  PauseCircle,
  SlidersHorizontal,
} from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

interface IllustrationProps {
  isHovered: boolean;
}

// Visual Builder - Hero card illustration (compact, fits 3-col hero)
export function VisualBuilderIllustration({ isHovered }: IllustrationProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isHovered) {
      interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % 4);
      }, 1500);
    } else {
      setActiveIndex(0);
    }
    return () => clearInterval(interval);
  }, [isHovered]);

  return (
    <div className="w-full h-full flex flex-col gap-3">
      {/* Mini canvas */}
      <div className="relative w-full flex-1 min-h-[120px] rounded-lg bg-background/50 border border-primary/20 overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-20" />
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0.25 }}
          transition={{ duration: 0.5 }}
        />

        {/* Nodes (4-step workflow) */}
        <div className="relative p-3 h-full flex items-center gap-2">
          {[
            {
              title: "Trigger",
              subtitle: "stripe.invoice.paid",
              tone: "bg-primary/10 border-primary/25",
              titleTone: "text-primary",
            },
            {
              title: "Filter",
              subtitle: "status == paid",
              tone: "bg-muted/40 border-border",
              titleTone: "text-foreground",
            },
            {
              title: "Transform",
              subtitle: "map → payload",
              tone: "bg-muted/40 border-border",
              titleTone: "text-foreground",
            },
            {
              title: "Action",
              subtitle: "POST /api/notify",
              tone: "bg-muted/40 border-border",
              titleTone: "text-foreground",
            },
          ].map((node, index) => (
            <div
              key={node.title}
              className="flex items-center gap-2 flex-1 min-w-0"
            >
              <motion.div
                initial={{ opacity: 0.65, y: 2 }}
                animate={
                  isHovered
                    ? {
                        opacity: 1,
                        y: 0,
                        scale: activeIndex === index ? 1.05 : 1,
                        borderColor:
                          activeIndex === index
                            ? "var(--color-primary)"
                            : "var(--color-border)",
                        backgroundColor:
                          activeIndex === index
                            ? "var(--color-primary-10)"
                            : undefined,
                        boxShadow:
                          activeIndex === index
                            ? "0 0 15px var(--color-primary-20)"
                            : "none",
                      }
                    : { opacity: 0.65, y: 2, scale: 1 }
                }
                transition={{ duration: 0.45 }}
                className={`min-w-0 rounded-lg border p-2 ${node.tone} relative overflow-hidden`}
              >
                {/* Processing shimmer */}
                {isHovered && activeIndex === index && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent"
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                )}

                <div className="flex items-center justify-between gap-2 relative z-10">
                  <span
                    className={`text-[10px] font-semibold ${node.titleTone}`}
                  >
                    {node.title}
                  </span>
                  {node.title === "Trigger" && (
                    <motion.span
                      className="inline-block w-2 h-2 rounded-full bg-primary"
                      animate={
                        isHovered ? { scale: [1, 1.3, 1] } : { scale: 1 }
                      }
                      transition={{
                        duration: 0.95,
                        repeat: isHovered ? Infinity : 0,
                        repeatDelay: 0.9,
                      }}
                    />
                  )}
                  {node.title === "Action" && (
                    <motion.span
                      className="text-[10px] font-mono text-success"
                      initial={{ opacity: 0.5 }}
                      animate={{
                        opacity: isHovered && activeIndex === 3 ? 1 : 0.5,
                      }}
                      transition={{ duration: 0.4 }}
                    >
                      ✓
                    </motion.span>
                  )}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground font-mono truncate relative z-10">
                  {node.subtitle}
                </div>
              </motion.div>

              {index < 3 && (
                <div className="w-6 flex-shrink-0">
                  <motion.div
                    className="h-0.5 rounded-full bg-border/60 overflow-hidden relative"
                    initial={{ opacity: 0.45 }}
                    animate={{ opacity: isHovered ? 1 : 0.45 }}
                    transition={{ duration: 0.45 }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/60 to-transparent"
                      initial={{ x: "-100%" }}
                      animate={
                        isHovered && activeIndex === index
                          ? { x: ["-100%", "200%"] }
                          : { x: "-100%" }
                      }
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  </motion.div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Run bar */}
      <div className="w-full rounded-lg bg-muted/30 border border-border px-3 py-2">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-8 h-8 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-mono text-xs"
            animate={
              isHovered
                ? {
                    scale: [1, 1.05, 1],
                    backgroundColor:
                      activeIndex % 2 === 0
                        ? "var(--color-primary-20)"
                        : "var(--color-primary-10)",
                  }
                : { scale: 1 }
            }
            transition={{ duration: 0.5, repeat: isHovered ? Infinity : 0 }}
          >
            <span>
              {isHovered ? (
                activeIndex === 0 ? (
                  <PauseCircle className="w-4 h-4" />
                ) : activeIndex === 1 ? (
                  <SlidersHorizontal className="w-4 h-4" />
                ) : activeIndex === 2 ? (
                  <Hourglass className="w-4 h-4" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )
              ) : (
                "▶"
              )}
            </span>
          </motion.div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-foreground">
                {activeIndex === 0
                  ? "Triggering..."
                  : activeIndex === 1
                    ? "Filtering..."
                    : activeIndex === 2
                      ? "Executing..."
                      : "Finalizing..."}
              </span>
              <motion.span
                className="text-[10px] font-mono text-muted-foreground"
                initial={{ opacity: 0.6 }}
                animate={{ opacity: isHovered ? 1 : 0.6 }}
                transition={{ duration: 0.4 }}
              >
                run_1042
              </motion.span>
            </div>
            <div className="mt-1 h-1.5 bg-border/50 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-primary/60"
                initial={{ width: "10%" }}
                animate={
                  isHovered
                    ? { width: `${25 + activeIndex * 25}%` }
                    : { width: "10%" }
                }
                transition={{ duration: 0.5, ease: "easeInOut" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Observability - Log viewer (fits medium card)
export function ObservabilityIllustration({ isHovered }: IllustrationProps) {
  const logs = [
    { status: "success", time: "10:32:18", message: "Trigger received" },
    { status: "success", time: "10:32:19", message: "Data processed" },
    { status: "success", time: "10:32:19", message: "Action complete" },
    { status: "running", time: "10:32:20", message: "Sending response..." },
  ];

  return (
    <div className="w-full h-full p-3 rounded-lg bg-background/50 border border-primary/20 font-mono text-[10px] flex flex-col justify-center gap-2">
      {logs.map((log, i) => (
        <motion.div
          key={i}
          className="flex items-center gap-2"
          initial={{ opacity: 0.4, x: -6 }}
          animate={isHovered ? { opacity: 1, x: 0 } : { opacity: 0.4, x: -6 }}
          transition={{ delay: i * 0.12, duration: 0.35 }}
        >
          {log.status === "success" ? (
            <span className="text-success text-xs">✓</span>
          ) : (
            <motion.span
              className="inline-block w-2.5 h-2.5 rounded-full bg-primary"
              animate={isHovered ? { scale: [1, 1.3, 1] } : { scale: 1 }}
              transition={{
                duration: 0.9,
                repeat: isHovered ? Infinity : 0,
                repeatDelay: 0.6,
              }}
            />
          )}
          <span className="text-muted-foreground">{log.time}</span>
          <span className="text-foreground truncate">{log.message}</span>
        </motion.div>
      ))}
    </div>
  );
}

// Webhooks - Horizontal flow (fits medium card)
export function WebhooksIllustration({ isHovered }: IllustrationProps) {
  return (
    <div className="w-full h-full rounded-lg bg-background/50 border border-primary/20 p-3 flex flex-col gap-3 overflow-hidden">
      {/* Incoming request */}
      <motion.div
        className="flex items-center gap-2 p-2 rounded-md bg-primary/10 border border-primary/30 font-mono text-[10px]"
        initial={{ opacity: 0.6, y: 4 }}
        animate={isHovered ? { opacity: 1, y: 0 } : { opacity: 0.6, y: 4 }}
        transition={{ duration: 0.4 }}
      >
        <span className="text-primary font-bold text-[9px] px-1.5 py-0.5 bg-primary/20 rounded">
          POST
        </span>
        <span className="text-muted-foreground truncate">
          /api/webhooks/stripe
        </span>
        <span className="ml-auto text-[9px] text-muted-foreground">
          invoice.paid
        </span>
      </motion.div>

      {/* Router */}
      <div className="relative flex-1 min-h-[110px] rounded-md bg-muted/30 border border-border overflow-hidden">
        <motion.div
          className="absolute group inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent pointer-events-none"
          initial={{ opacity: 0.2 }}
          animate={{ opacity: isHovered ? 1 : 0.2 }}
          transition={{ duration: 0.6 }}
        />

        <div className="flex items-center w-full justify-between px-2 mt-1">
          <span className="text-[10px] font-semibold text-foreground">
            Router
          </span>
          <span className="text-[9px] font-mono text-muted-foreground">
            match → fanout
          </span>
        </div>
        <div className="mt-1 top-5 left-5 absolute group-hover:opacity-50 transition-opacity duration-200 rounded bg-background/40 border border-border px-2 font-mono text-[9px] text-muted-foreground leading-relaxed">
          <div className="truncate">
            verify <span className="text-foreground">Stripe-Signature</span>
          </div>
          <div className="truncate">
            dedupe by <span className="text-foreground">event.id</span>
          </div>
          <div className="truncate">
            route by <span className="text-foreground">event.type</span>
          </div>
          <div className="truncate">
            retries: <span className="text-foreground">exp backoff</span>
          </div>
        </div>
        <div className="relative h-full p-2 flex gap-3">
          {/* Logic Block */}
          <div className="flex-1 flex flex-col justify-between min-w-0"></div>

          {/* Destinations (Vertical Fanout) */}
          <div className="flex flex-col gap-1.5 mt-10 mr-3  justify-center relative z-10">
            {[
              {
                label: "Slack",
                tone: "bg-primary/10 border-primary/20",
                meta: "#billing",
              },
              {
                label: "DB",
                tone: "bg-muted/40 border-border",
                meta: "events",
              },
              {
                label: "Email",
                tone: "bg-muted/40 border-border",
                meta: "finance@",
              },
            ].map((dest, i) => (
              <motion.div
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={`rounded-md border ${dest.tone} px-2 py-1 flex items-center justify-between gap-2 min-w-[80px]`}
                initial={{ opacity: 0.5, x: 10 }}
                animate={
                  isHovered ? { opacity: 1, x: 0 } : { opacity: 0.5, x: 10 }
                }
                transition={{ delay: i * 0.1 + 0.2, duration: 0.4 }}
              >
                <div className="min-w-0">
                  <div className="text-[9px] font-semibold text-foreground truncate">
                    {dest.label}
                  </div>
                  <div className="text-[8px] font-mono text-muted-foreground truncate">
                    {dest.meta}
                  </div>
                </div>
                <motion.span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-primary"
                  initial={{ opacity: 0.3 }}
                  animate={
                    isHovered ? { opacity: [0.3, 1, 0.3] } : { opacity: 0.3 }
                  }
                  transition={{
                    duration: 1.2,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              </motion.div>
            ))}
          </div>

          {/* Branching Packets */}
          {isHovered &&
            [0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="absolute left-4 top-3/5 -translate-y-4/5 w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_var(--color-primary)]"
                initial={{ opacity: 0, x: 0, y: 0 }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  x: [0, 40, 80, 120],
                  y: [0, 0, (i - 1) * 32, (i - 1) * 32],
                }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  repeatDelay: 0.4,
                  delay: i * 0.15,
                  ease: "easeInOut",
                }}
              />
            ))}
        </div>
      </div>

      {/* Response */}
      <motion.div
        className="flex items-center gap-2 p-2 rounded-md bg-success/10 border border-success/30 font-mono text-[10px]"
        initial={{ opacity: 0.6, y: -2 }}
        animate={isHovered ? { opacity: 1, y: 0 } : { opacity: 0.6, y: -2 }}
        transition={{ duration: 0.45, delay: 0.15 }}
      >
        <span className="text-success font-bold px-1.5 py-0.5 bg-success/20 rounded text-[9px]">
          200
        </span>
        <span className="text-success">Delivered</span>
        <span className="text-muted-foreground ml-auto">~32ms</span>
      </motion.div>
    </div>
  );
}

// Retries - Horizontal flow (fits small card in row 2)
export function RetriesIllustration({ isHovered }: IllustrationProps) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-3">
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((num, i) => (
          <div key={i} className="flex items-center">
            <motion.div
              className="w-9 h-9 rounded-lg flex flex-col items-center justify-center border-2 relative overflow-hidden"
              style={{ borderColor: "var(--color-primary)" }}
              animate={
                isHovered
                  ? i < 2
                    ? {
                        borderColor: [
                          "var(--color-primary-20)",
                          "var(--color-error-50)",
                          "var(--color-primary-20)",
                        ],
                        backgroundColor: [
                          "transparent",
                          "var(--color-error-10)",
                          "transparent",
                        ],
                      }
                    : {
                        borderColor: "var(--color-success-50)",
                        backgroundColor: "var(--color-success-10)",
                      }
                  : {}
              }
              transition={{ delay: i * 0.4, duration: 0.5 }}
            >
              <span className="text-[10px] font-mono font-bold text-foreground z-10">
                {i === 2 && isHovered ? "✓" : num}
              </span>
              {/* Progress bar inside node */}
              {isHovered && i === 2 && (
                <motion.div
                  className="absolute bottom-0 left-0 h-1 bg-success"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ delay: 1.2, duration: 0.4 }}
                />
              )}
            </motion.div>
            {i < 2 && (
              <div className="w-4 h-[2px] bg-border relative">
                <motion.div
                  className="absolute inset-0 bg-primary"
                  initial={{ scaleX: 0, originX: 0 }}
                  animate={isHovered ? { scaleX: 1 } : { scaleX: 0 }}
                  transition={{ delay: i * 0.4 + 0.2, duration: 0.2 }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-4 md:gap-8 text-[8px] font-mono text-muted-foreground">
        <motion.span animate={isHovered ? { opacity: 1 } : { opacity: 0.4 }}>
          T+1s
        </motion.span>
        <motion.span
          animate={isHovered ? { opacity: 1 } : { opacity: 0.4 }}
          transition={{ delay: 0.4 }}
        >
          T+2s
        </motion.span>
        <motion.span
          animate={isHovered ? { opacity: 1 } : { opacity: 0.4 }}
          transition={{ delay: 0.8 }}
        >
          T+4s
        </motion.span>
      </div>
    </div>
  );
}

// Pricing - Compact (fits small card in row 2)
export function PricingIllustration({ isHovered }: IllustrationProps) {
  return (
    <div className="w-full h-full flex flex-col justify-center lg:-mt-6 gap-3 px-2">
      <div className="space-y-1.5">
        <div className="flex justify-between text-[11px]">
          <span className="text-muted-foreground">Monthly runs</span>
          <motion.span
            className="font-mono text-foreground"
            initial={{ opacity: 0.5 }}
            animate={isHovered ? { opacity: 1 } : {}}
          >
            2.8k / 10k
          </motion.span>
        </div>
        <div className="h-2.5 bg-muted/50 rounded-full overflow-hidden border border-primary/20">
          <motion.div
            className="h-full rounded-full"
            style={{
              background:
                "linear-gradient(90deg, hsl(200 95% 48%), hsl(220 90% 55%))",
            }}
            initial={{ width: "8%" }}
            animate={isHovered ? { width: "28%" } : { width: "8%" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
      </div>
      <motion.div
        className="flex items-center justify-center py-1.5 px-3 rounded-md bg-primary/10 border border-primary/30"
        initial={{ scale: 0.95, opacity: 0.5 }}
        animate={isHovered ? { scale: 1, opacity: 1 } : {}}
        transition={{ delay: 0.3, duration: 0.3 }}
      >
        <span className="text-[11px] font-semibold text-primary">
          Free tier included
        </span>
      </motion.div>
    </div>
  );
}

// Production-Grade - Compact status (fits small card in row 2)
export function ProductionGradeIllustration({ isHovered }: IllustrationProps) {
  return (
    <div className="w-full h-full p-3 rounded-lg bg-background/50 border border-primary/20 flex flex-col justify-center">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-3 h-3 rounded-full bg-success"
            animate={isHovered ? { scale: [1, 1.3, 1] } : {}}
            transition={{
              duration: 1.1,
              repeat: isHovered ? Infinity : 0,
              repeatDelay: 0.6,
            }}
          />
          <span className="text-[11px] font-medium text-foreground">
            Operational
          </span>
        </div>
        <motion.span
          className="text-[10px] text-primary font-mono"
          initial={{ opacity: 0.5 }}
          animate={isHovered ? { opacity: 1 } : {}}
        >
          99.99%
        </motion.span>
      </div>

      {/* Mini uptime chart */}
      <div className="flex gap-0.5 h-6">
        {Array.from({ length: 14 }).map((_, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-sm"
            initial={{
              height: "25%",
              backgroundColor: "hsl(200 95% 48% / 0.3)",
            }}
            animate={
              isHovered
                ? {
                    height: i === 10 ? "50%" : "100%",
                    backgroundColor:
                      i === 10 ? "hsl(38 92% 55%)" : "hsl(200 95% 48% / 0.6)",
                  }
                : {}
            }
            transition={{ delay: i * 0.03, duration: 0.35 }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[9px] text-muted-foreground">30 days</span>
        <span className="text-[9px] text-muted-foreground">Now</span>
      </div>
    </div>
  );
}
